import React, { useEffect, useMemo } from "react";
import "./WeeklyReport.css";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Breadcrumb from "../../../../components/custom-breadcrumb/Breadcrumb";
import { AppDispatch, RootState } from "../../../../redux/store/store";
import { fetchCmsReport } from "../../../../redux/slices/userListSlice";
import { formatDateTime } from "../../../../utils/formatDateTime";
import Loader from "../../../../components/custom-loader/Loader";
import { CmsReportQuestion } from "../../../../apis/userApis";

const WeeklyReport: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { name, userDetailId, reportId } = useParams<{
    name: string;
    userDetailId: string;
    reportId: string;
  }>();
  const { cmsReport, cmsReportLoading } = useSelector(
    (state: RootState) => state.userList,
  );

  useEffect(() => {
    if (reportId) dispatch(fetchCmsReport({ weekReportMappingId: reportId }));
  }, [dispatch, reportId]);

  const questions = useMemo(() => {
    if (!cmsReport) return [];

    const reportDetails = Array.isArray(cmsReport.reportDetails)
      ? cmsReport.reportDetails
      : [];

    // Support both the existing flat question list and category-wrapped questions.
    const reportQuestions = reportDetails.flatMap((detail) =>
      "questions" in detail && Array.isArray(detail.questions)
        ? detail.questions
        : [detail as CmsReportQuestion],
    );

    return reportQuestions
      .slice()
      .sort((a, b) => a.questionSortOrder - b.questionSortOrder)
      .map((q) => ({
        id: q.questionId,
        text: q.questionName,
        options: (Array.isArray(q.options) ? q.options : [])
          .slice()
          .sort((a, b) => a.optionSortOrder - b.optionSortOrder)
          .map((opt) => ({
            id: opt.optionId,
            text: opt.optionName,
            selected: opt.optionName === q.answer,
          })),
      }));
  }, [cmsReport]);

  const breadcrumbItems = [
    { label: "Users", onClick: () => navigate("/users") },
    {
      label: name ?? "",
      onClick: () => navigate(`/users/${name}/${userDetailId}`),
    },
    { label: "Weekly Report", disabled: true },
  ];

  if (cmsReportLoading) return <Loader />;

  return (
    <>
      <Breadcrumb items={breadcrumbItems} />
      <div className="weekly-report-header">
        <h1 className="page-title">Weekly Report</h1>
      </div>
      <div className="weekly-report">
        <div className="report-header">
          <div className="report-header-top">
            <h2 className="report-title">
              Weekly Progress Report - {cmsReport?.reports?.weeks?.name}
            </h2>
            <div className="report-meta">
              Submitted On: {formatDateTime(cmsReport?.createdAt || "")}
            </div>
          </div>
        </div>
        <section className="report-section">
          <div className="section-body">
            {questions.map((q, index) => (
              <div className="question" key={q.id}>
                <div className="q-text">
                  <span className="q-number">Q{index + 1}.</span>
                  <span className="q-label">{q.text}</span>
                </div>
                <div className="chips" role="list">
                  {q.options
                    .filter((opt) => opt.text?.trim() !== "")
                    .map((opt) => (
                      <span
                        key={opt.id}
                        role="listitem"
                        className={`chip ${opt.selected ? "selected" : ""}`}
                        aria-pressed={opt.selected ? "true" : "false"}
                      >
                        {opt.text}
                      </span>
                    ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </>
  );
};

export default WeeklyReport;
